import logger from'xp-node-logger';import assert from'assert';import {setImmediate}from'timers';/**
 * @typedef {Object} Context
 * @property {string} storeId - The Store identification string
 * @property {Object} cookies - cookie key and value pairs
 */

/**
 * Get persistent user id from the cookies within the apollo context. Will pull the id
 * corresponding to the store id matched within the context.
 * @param {Context} context
 * @example
 * // returns 0123456789
 * getPersistentUserId({
 *  storeId: 10051,
 *  cookies: { WC_PERSISTENT: '...10051_0123456789' } }
 * );
 * @example
 * // returns null
 * getPersistentUserId({
 *  storeId: 10051,
 *  cookies: { ... }
 * );
 * @returns {String|null} Returns string representing the persistent user id
 */
const getPersistentUserId = (context = {}) => {
  const { cookies, storeId } = context;

  if (!cookies) return null;

  const cookie = cookies.WCP_PERSISTENT || cookies.WC_PERSISTENT;
  const match = cookie?.match(new RegExp(`${storeId}_[-{0-9}]*[0-9]+`));

  if (!match) return null;

  const userId = match[0].split('_')[1];

  return userId !== '-1002' ? userId : null;
};

/**
 * isStorePreview is a general use function to identify the store preview site
 * @param {Context} context
 * @returns {Boolean} - whether store preview cookies are present or not
 */
const isStorePreview = (context = {}) => {
  const { cookies } = context;

  if (!cookies) return false;

  return Object.keys(cookies).filter((key) => key.includes('WCP')).length > 0;
};

/**
 * Get user id from the apollo context. Using the WC_USERACTIVITY cookie it'll pull
 * the corresponding value that matches the context store id.
 * Note: there can be multiple WC_USERACTIVITY cookies on the page. One for each
 * country visited. Therefore, we need to loop over all possibilities.
 * @param {Context} context
 * @example
 * // returns 0123456789
 * getActiveUserId({
 *  storeId: 10051,
 *  cookies: { WC_USERACTIVITY_0123456789: '10051,0123456789...' } }
 * );
 * @example
 * // returns null
 * getActiveUserId({
 *  storeId: 10051,
 *  cookies: { ... }
 * );
 * @returns {String|null} Returns string representing the user id
 */
const getActiveUserId = (context = {}) => {
  const { cookies, storeId } = context;
  const isPreview = isStorePreview(context);

  const pullUserIdAndStoreFromUserActivityCookie = (cookie) => {
    const [userId, currentStoreId] = decodeURIComponent(cookie).split(',');
    return [userId, currentStoreId];
  };

  if (!cookies) return null;

  const activityCookies = Object.entries(cookies).reduce((acc, [key, cookie]) => {
    const cookieSubKey = !isPreview ? 'WC_USERACTIVITY' : 'WCP_USERACTIVITY';

    if (key.includes(cookieSubKey)) {
      acc.push(pullUserIdAndStoreFromUserActivityCookie(cookie));
    }

    return acc;
  }, []);

  if (!activityCookies.length) return null;

  const [userId] = activityCookies.find(
    ([, currentStoreId]) => currentStoreId === storeId?.toString(),
  ) || [null];

  return userId === '-1002' ? null : userId;
};

/**
 * getSessionId - returns the current session id derived from cookies or null
 * @param {Context} context
 * @returns {String|null}
 */
const getSessionId = (context = {}) => getPersistentUserId(context) ?? getActiveUserId(context);

/**
 * getUserType - returns the user type from cookies or null
 * @param {Context} context
 * @returns {String|null}
 */
const getUserType = (context = {}) => {
  const { cookies } = context;
  if (!cookies) return null;

  const { userType } = cookies;

  if (!userType) {
    // if there's a session id, but no userType, log debug message
    if (getSessionId(context) !== null) {
      logger.debug('Missing userType', { context });
    }
    return null;
  }

  return userType;
};var cookies=/*#__PURE__*/Object.freeze({__proto__:null,getActiveUserId:getActiveUserId,getPersistentUserId:getPersistentUserId,getSessionId:getSessionId,getUserType:getUserType,isStorePreview:isStorePreview});/**
 * Executes a loop until the specified condition is false, performing the specified work on each
 * iteration. It provides a non-blocking way to execute CPU-bound work in a loop.
 * This method offers:
 * 1. Flexibility: The loopy() function is highly flexible because it accepts two functions as
 * parameters: condition and work. This allows you to define the loop's condition and the work
 * done in each iteration according to your specific needs.
 * 2. Asynchronous: The function is designed to handle asynchronous operations. If the work
 * function returns a Promise, loopy() will wait for it to resolve before proceeding to the next
 * iteration. This is useful when dealing with operations that involve I/O, such as network requests
 * or file system operations.
 * 3. Non-blocking: The function uses setImmediate() to schedule the next iteration of the loop.
 * This means that it won't block the event loop, allowing other operations to run in between
 * iterations.
 * @param {Function} condition - The condition function that determines whether to continue the loop.
 * @param {Function} work - The work function to be executed on each iteration of the loop.
 * @returns {Promise<void>} A promise that resolves when the loop is completed or rejects if an error occurs.
 */

async function loopy(condition, work) {
  assert(typeof condition === 'function', 'condition must be a function');
  assert(typeof work === 'function', 'work must be a function');

  return new Promise((resolve, reject) => {
    const loop = async () => {
      try {
        if (!condition()) {
          resolve();
          return;
        }
        const result = work();
        if (result instanceof Promise) {
          await result;
        }
        setImmediate(loop);
      } catch (e) {
        reject(e);
      }
    };
    loop();
  });
}var loopy$1=/*#__PURE__*/Object.freeze({__proto__:null,default:loopy});// List of store IDs for each brand, supported by A&F and HCo websites.
const STORE_IDS = [
  10051, // A&F US
  11203, // A&F WD
  11306, // A&F CA
  19159, // A&F EU
  19658, // A&F UK
  21657, // A&F AM
  10251, // HCo US
  11205, // HCo WD
  11308, // HCo CA
  19158, // HCo EU
  19659, // HCo UK
  21658, // HCo AM
];

// regex pattern that matches any string starting with a or h,
// followed by a hyphen (-), then any two letters,
// optionally followed by another hyphen and any two letters
// e.g. a-us, h-uk, a-ca, h-am, a-us-es, h-uk-en etc.
const STORE_PATTERN = /^[ah]-[a-z]{2}(-[a-z]{2})?$/;

// List of supported brands
const STORE_BRANDS = [
  'anf',
  'hol',
  'kids',
];

// List of MFE pages, currently only lists supported page types used by `checkout-mfe`
const MFE_PAGES = ['mini', 'bag', 'checkout', 'ocp'];

// List of filters supported in cart API
const CART_API_FILTERS = ['condensedDetails', 'details'];/**
 * Sanitizes the store ID.
 *
 * @param {number} storeId - The store ID to sanitize.
 * @returns {number} - The sanitized store ID.
 */
const sanitizeStoreId = (storeId) => {
  // Parse the storeId as an integer
  const parsedStoreId = parseInt(storeId, 10);
  // Check if the parsedStoreId is NaN or not in the STORE_IDS
  if (Number.isNaN(parsedStoreId) || !STORE_IDS.includes(parsedStoreId)) {
    logger.debug('contextFromRequest', 'sanitizeStoreId', 'Invalid Store Id, so returned 10051', { storeId });
    // Return A&F US 10051 for invalid storeId
    return 10051;
  }
  // Return the valid parsedStoreId
  return parsedStoreId;
};

/**
 * Sanitizes the language ID.
 *
 * @param {number} langId - The language ID to sanitize.
 * @returns {number} - The sanitized language ID.
 */
const sanitizeLangId = (langId) => {
  // Parse the langId as a floating-point number
  const parsedLangId = parseFloat(langId);
  // Check if the parsedLangId is NaN
  if (Number.isNaN(parsedLangId)) {
    logger.debug('contextFromRequest', 'sanitizeLangId', 'Invalid Lang Id, so returned -1', { langId });
    // Return English (-1) for invalid langId
    return -1;
  }
  // Return the valid parsedLangId
  return parsedLangId;
};

/**
 * Sanitizes the language.
 *
 * @param {number} langId - The language ID to sanitize.
 * @returns {number} - The sanitized language ID.
 */
const sanitizeLanguage = (language) => {
  // Check if the language is not a string or contains non-alphabetic characters
  if (typeof language !== 'string' || !/^[a-zA-Z]+$/.test(language)) {
    logger.debug('contextFromRequest', 'sanitizeLanguage', 'Invalid Language, so returned -1', { language });
    // Return 'en' for invalid language
    return 'en';
  }
  // Return the valid language
  return language;
};

/**
 * Sanitizes the catalog ID.
 *
 * @param {number} catalogId - The catalog ID to sanitize.
 * @returns {number} - The sanitized catalog ID.
 */
const sanitizeCatalogId = (catalogId) => {
  // Parse the catalogId as an integer
  const parsedCatalogId = parseInt(catalogId, 10);
  // Check if the parsedCatalogId is NaN
  if (Number.isNaN(parsedCatalogId)) {
    logger.debug('contextFromRequest', 'sanitizeCatalogId', 'Invalid Catalog Id, so returned 10901', { catalogId });
    // Return 10901 for invalid catalogId
    return 10901;
  }
  // Return the valid parsedCatalogId
  return parsedCatalogId;
};

/**
 * Sanitizes the brand.
 *
 * @param {string} brand - The brand to sanitize.
 * @returns {string} - The sanitized brand.
 */
const sanitizeBrand = (brand) => {
  const sanitizedBrand = typeof brand === 'string' ? brand.toLowerCase() : 'anf';
  // Check if the brand is not in the list of brands
  if (!STORE_BRANDS.includes(sanitizedBrand)) {
    logger.debug('contextFromRequest', 'sanitizeBrand', 'Invalid Brand, so returned anf', { brand });
    // Return anf for invalid brand
    return 'anf';
  }
  // Return the valid brand
  return sanitizedBrand;
};

/**
 * Sanitizes the store.
 *
 * @param {string} store - The store to sanitize.
 * @returns {string} - The sanitized store.
 */
const sanitizeStore = (store) => {
  // regex pattern that matches any string starting with a or h,
  // followed by a hyphen (-), then any two letters,
  // optionally followed by another hyphen and any two letters
  if (!STORE_PATTERN.test(store)) {
    logger.debug('contextFromRequest', 'sanitizeStore', 'Invalid Store, so returned a-us', { store });
    // Return A&F US when invalid store is passed
    return 'a-us';
  }
  // Return the valid store
  return store;
};

/**
 * Sanitizes the country.
 *
 * @param {string} country - The country to sanitize.
 * @returns {string} - The sanitized country.
 */
const sanitizeCountry = (country) => {
  // Check if the country is not a string or contains non-alphabetic characters
  if (typeof country !== 'string' || !/^[a-zA-Z]+$/.test(country)) {
    logger.debug('contextFromRequest', 'sanitizeCountry', 'Invalid Country, so returned US', { country });
    // Return US for invalid country
    return 'US';
  }
  // Return the valid country
  return country;
};

/**
 * Sanitizes the page.
 *
 * @param {string} page - The page to sanitize.
 * @returns {string} - The sanitized page.
 */
const sanitizePage = (page) => {
  // Ensure the page is a string and one of the allowed values
  if (typeof page !== 'string' || !MFE_PAGES.includes(page.toString().toLowerCase())) {
    logger.debug('contextFromRequest', 'sanitizePage', 'Invalid Page, so returned "checkout"', { page });
    return 'checkout';
  }

  // Convert the page to lowercase and return it
  return page.toLowerCase();
};

/**
 * Sanitizes the filter.
 *
 * @param {string} filter - The filter to sanitize.
 * @returns {string} - The sanitized filter.
 */
const sanitizeFilter = (filter) => {
  // Ensure the filter is a string and one of the allowed values
  if (typeof filter !== 'string' || !CART_API_FILTERS.includes(filter)) {
    logger.debug('contextFromRequest', 'sanitizeFilter', 'Invalid Filter, so returned "condensedDetails"', { filter });
    // If the filter is not valid, return 'condensedDetails'
    return 'condensedDetails';
  }

  // Return the valid filter
  return filter;
};var sanitize=/*#__PURE__*/Object.freeze({__proto__:null,sanitizeBrand:sanitizeBrand,sanitizeCatalogId:sanitizeCatalogId,sanitizeCountry:sanitizeCountry,sanitizeFilter:sanitizeFilter,sanitizeLangId:sanitizeLangId,sanitizeLanguage:sanitizeLanguage,sanitizePage:sanitizePage,sanitizeStore:sanitizeStore,sanitizeStoreId:sanitizeStoreId});// eslint-disable-next-line import/prefer-default-export
const getSignifydSessionID = (document) => {
  if (!document) return null;

  return document.getElementById('sig-api')?.getAttribute('data-order-session-id') || null;
};var signifyd=/*#__PURE__*/Object.freeze({__proto__:null,getSignifydSessionID:getSignifydSessionID});/**
 * state - Capturing common user state functions

 * isGeneric = WC_PERSISTENT == <storeId>_-1002
 * isGuestUser = !isGeneric && !isLoggedIn
 * isLoggedIn = userType == 'R'
*/


/**
 * isGeneric is a general use function to identify a generic user
 * @param {Context} context
 * @returns {Boolean} - boolean for if they are generic or not
 */
function isGeneric(context = {}) {
  const userId = getSessionId(context);
  return !userId;
}

/**
 * isLoggedIn is a general use function to identify whether user is logged in or not
 * @param {Context} context
 * @returns {Promise<Boolean>} - boolean for if they are logged in or not
 */
async function isLoggedIn(context = {}) {
  const userTypeCookie = getUserType(context);
  if (userTypeCookie) return Promise.resolve(!isGeneric(context) && userTypeCookie === 'R');

  const { sessionSource } = context?.dataSources || {};
  if (!sessionSource?.info) return Promise.resolve(false);

  // Use session/info call as a fallback for checking the users logged in state
  return sessionSource.info(context)
    .then(({ userType }) => userType.toLowerCase() === 'registered')
    .catch(() => false);
}

/**
 * isGuest is a general use function to identify whether user is guest or not
 * @param {Context} context
 * @returns {Promise<Boolean>} - boolean for if they are guest or not
 */
async function isGuest(context = {}) {
  const isGenericUser = isGeneric(context);
  const isLoggedInUser = await isLoggedIn(context); // will always resolve Boolean
  return !isGenericUser && !isLoggedInUser;
}var state=/*#__PURE__*/Object.freeze({__proto__:null,isGeneric:isGeneric,isGuest:isGuest,isLoggedIn:isLoggedIn});export{cookies,loopy$1 as loopy,sanitize,signifyd,state};